import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Trophy, Star, Shield, Zap,
  ChevronRight, ChevronDown, ChevronUp, Mail, Trash2,
  Upload, X, Crown, Bell, CheckCircle, XCircle, Swords
} from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { authAPI } from '../api';

const DD_VERSION = '16.3.1';
const DD_BASE = `https://ddragon.leagueoflegends.com/cdn/${DD_VERSION}`;

let championMap = null;
async function getChampionMap() {
  if (championMap) return championMap;
  try {
    const res = await fetch(`${DD_BASE}/data/fr_FR/champion.json`);
    const json = await res.json();
    championMap = {};
    Object.values(json.data).forEach(c => {
      championMap[parseInt(c.key)] = c.id;
    });
  } catch { championMap = {}; }
  return championMap;
}

function tierColor(tier) {
  const colors = {
    IRON: '#7c8594', BRONZE: '#8c5a3c', SILVER: '#8fa3a8',
    GOLD: '#d4a84b', PLATINUM: '#4fa67f', EMERALD: '#2ecc71',
    DIAMOND: '#4d8eff', MASTER: '#9b59b6', GRANDMASTER: '#e74c3c',
    CHALLENGER: '#f1c40f', UNRANKED: '#4a5568'
  };
  return colors[tier] || '#4a5568';
}

function tierLabel(q) {
  if (!q || q.tier === 'UNRANKED' || !q.tier) return 'Non classé';
  return `${q.tier} ${q.rank}`;
}

function formatMastery(pts) {
  if (pts >= 1000000) return `${(pts / 1000000).toFixed(1)}M`;
  if (pts >= 1000) return `${(pts / 1000).toFixed(0)}k`;
  return `${pts}`;
}

function winRate(entry) {
  if (!entry || (entry.wins + entry.losses) === 0) return null;
  const total = entry.wins + entry.losses;
  return { wr: Math.round((entry.wins / total) * 100), wins: entry.wins, losses: entry.losses, total };
}

export default function DashboardPage() {
  const { user } = useContext(AuthContext);
  const [teams, setTeams]         = useState([]);
  const [stats, setStats]         = useState(null);
  const [champNames, setChampNames] = useState({});
  const [loadingTeams, setLoadingTeams]   = useState(true);
  const [loadingStats, setLoadingStats]   = useState(true);
  const [invitations, setInvitations]     = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTeam, setNewTeam]     = useState({ name: '', tag: '', logo: null });
  const [logoPreview, setLogoPreview]   = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteTeamId, setInviteTeamId] = useState(null);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadTeams(); loadStats(); loadInvitations(); }, []);

  const showMessage = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const loadTeams = async () => {
    try { const r = await authAPI.getMyTeams(); setTeams(r.data || []); }
    catch (e) { console.error(e); }
    finally { setLoadingTeams(false); }
  };

  const loadStats = async () => {
    try {
      const r = await authAPI.getRiotStats();
      setStats(r.data);
      if (r.data.masteries?.length) {
        const map = await getChampionMap();
        setChampNames(map);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingStats(false); }
  };

  const loadInvitations = async () => {
    try { const r = await authAPI.getPendingInvitations(); setInvitations(r.data || []); }
    catch (e) { console.error(e); }
    finally { setLoadingInvites(false); }
  };

  const handleLogoFile = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const res = await authAPI.uploadTeamLogo(file);
      setNewTeam(p => ({ ...p, logo: res.data.url }));
      setLogoPreview(URL.createObjectURL(file));
    } catch (e) { showMessage('Erreur upload logo: ' + e.message, true); }
    finally { setLogoUploading(false); }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await authAPI.createTeam(newTeam.name, newTeam.tag, newTeam.logo);
      setNewTeam({ name: '', tag: '', logo: null });
      setLogoPreview(null);
      setShowCreateForm(false);
      showMessage('Équipe créée avec succès !');
      loadTeams();
    } catch (err) { showMessage(err.message || 'Erreur lors de la création', true); }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Supprimer cette équipe ?')) return;
    try { await authAPI.deleteTeam(teamId); showMessage('Équipe supprimée'); loadTeams(); }
    catch (err) { showMessage(err.message || 'Erreur lors de la suppression', true); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await authAPI.inviteToTeam(inviteTeamId, inviteEmail);
      setInviteEmail(''); setInviteTeamId(null);
      showMessage('Invitation envoyée !');
    } catch (err) { showMessage(err.message || "Erreur lors de l'invitation", true); }
  };

  const handleAcceptInvitation = async (token) => {
    try {
      await authAPI.acceptInvitation(token);
      showMessage("Invitation acceptée — bienvenue dans l'équipe !");
      loadInvitations(); loadTeams();
    } catch (e) { showMessage(e.message || "Erreur lors de l'acceptation", true); }
  };

  const handleDeclineInvitation = async (token) => {
    try {
      await authAPI.declineInvitation(token);
      showMessage('Invitation refusée.');
      loadInvitations();
    } catch (e) { showMessage(e.message || 'Erreur lors du refus', true); }
  };

  const topChamp = stats?.masteries?.[0];
  const topChampName = topChamp ? champNames[topChamp.championId] : null;
  const splashUrl = topChampName
    ? `https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${topChampName}_0.jpg`
    : null;
  const profileIconUrl = stats?.profileIconId
    ? `${DD_BASE}/img/profileicon/${stats.profileIconId}.png` : null;
  const soloWR = winRate(stats?.soloQueue);
  const flexWR = winRate(stats?.flex);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="dashboard-page">

      {/* ══════════ PROFILE HERO ══════════ */}
      <div className="profile-hero">
        {splashUrl && (
          <div className="profile-hero-bg" style={{ backgroundImage: `url(${splashUrl})` }} />
        )}
        <div className="profile-hero-overlay" />
        <div className="profile-hero-content">
          <div className="profile-icon-wrapper">
            {profileIconUrl
              ? <img src={profileIconUrl} alt="Icône profil" className="profile-icon" />
              : <div className="profile-icon-placeholder"><Shield size={40} /></div>}
            <span className="profile-level">{stats?.level || user?.level || '?'}</span>
          </div>

          <div className="profile-info">
            <h1 className="profile-name">
              {user?.displayName || user?.riotId}
              {user?.tagLine && <span className="profile-tag">#{user.tagLine}</span>}
            </h1>
            <p className="profile-region">{(stats?.region || user?.region || '').toUpperCase()}</p>
            <div className="profile-ranks-row">
              {loadingStats ? (
                <div className="rank-badge loading-rank">Chargement…</div>
              ) : (
                <>
                  <div className="rank-badge-group">
                    <span className="rank-queue-label">Solo/Duo</span>
                    <div className="rank-badge"
                      style={{ borderColor: tierColor(stats?.soloQueue?.tier), color: tierColor(stats?.soloQueue?.tier) }}>
                      <Trophy size={13} />
                      {stats?.soloQueue ? tierLabel(stats.soloQueue) : 'Non classé'}
                      {stats?.soloQueue?.leaguePoints !== undefined && (
                        <span className="rank-lp">{stats.soloQueue.leaguePoints} LP</span>
                      )}
                    </div>
                  </div>
                  <div className="rank-badge-group">
                    <span className="rank-queue-label">Flex</span>
                    <div className="rank-badge"
                      style={{ borderColor: tierColor(stats?.flex?.tier), color: tierColor(stats?.flex?.tier) }}>
                      <Trophy size={13} />
                      {stats?.flex ? tierLabel(stats.flex) : 'Non classé'}
                      {stats?.flex?.leaguePoints !== undefined && (
                        <span className="rank-lp">{stats.flex.leaguePoints} LP</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="profile-wr-group">
            {soloWR && (
              <div className="profile-wr-card">
                <div className="wr-label">Solo/Duo</div>
                <div className="wr-value" style={{ color: soloWR.wr >= 50 ? '#10b981' : '#ef4444' }}>
                  {soloWR.wr}%
                </div>
                <div className="wr-detail">{soloWR.wins}V {soloWR.losses}D</div>
              </div>
            )}
            {flexWR && (
              <div className="profile-wr-card">
                <div className="wr-label">Flex</div>
                <div className="wr-value" style={{ color: flexWR.wr >= 50 ? '#10b981' : '#ef4444' }}>
                  {flexWR.wr}%
                </div>
                <div className="wr-detail">{flexWR.wins}V {flexWR.losses}D</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="dash-alert dash-alert--error">❌ {error}</motion.div>
        )}
        {success && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="dash-alert dash-alert--success">✅ {success}</motion.div>
        )}
      </AnimatePresence>

      {/* ══════════ MASTERY — full width horizontal scroll ══════════ */}
      {!loadingStats && stats?.masteries?.length > 0 && (
        <div className="dash-section-wrap">
          <div className="dash-section-header">
            <h2 className="dash-section-title"><Star size={18} /> Champions maîtrisés</h2>
          </div>
          <div className="mastery-scroll">
            {stats.masteries.map((m, i) => {
              const name = champNames[m.championId];
              const splash = name
                ? `https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${name}_0.jpg`
                : null;
              return (
                <motion.div
                  key={m.championId}
                  className={`mastery-card-v2${i === 0 ? ' mastery-card-v2--top' : ''}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -4 }}
                >
                  {splash
                    ? <div className="mastery-card-v2-bg" style={{ backgroundImage: `url(${splash})` }} />
                    : <div className="mastery-card-v2-bg" style={{ background: 'rgba(20,12,34,0.8)' }} />}
                  <div className="mastery-card-v2-overlay" />
                  {i === 0 && <span className="mastery-card-v2-top-badge">Principal</span>}
                  <div className="mastery-card-v2-body">
                    <div className="mastery-card-v2-name">{name || `#${m.championId}`}</div>
                    <div className="mastery-card-v2-level"><Zap size={11} /> Niv. {m.championLevel}</div>
                    <div className="mastery-card-v2-pts">{formatMastery(m.championPoints)} pts</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════ 2-COLUMN LAYOUT ══════════ */}
      <div className="dashboard-layout">

        {/* ── MAIN: Mes Équipes ── */}
        <div className="dashboard-main">
          <section className="dash-card">
            <div className="dash-section-header">
              <h2 className="dash-section-title"><Shield size={18} /> Mes Équipes</h2>
              <button className="action-button" onClick={() => setShowCreateForm(!showCreateForm)}>
                <Plus size={16} /> Créer une équipe
              </button>
            </div>

            {/* Create form */}
            <AnimatePresence>
              {showCreateForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="create-team-form" style={{ marginBottom: '1.25rem' }}>
                  <form onSubmit={handleCreateTeam}>
                    <div className="create-team-logo-row">
                      <div
                        className={`create-team-logo-zone${logoUploading ? ' uploading' : ''}`}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {logoPreview
                          ? <img src={logoPreview} alt="logo preview" className="create-team-logo-preview" />
                          : logoUploading
                            ? <div className="create-team-logo-spinner" />
                            : <><Upload size={18} /><span>Logo</span></>}
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => handleLogoFile(e.target.files?.[0])} />
                      {logoPreview && (
                        <button type="button" className="cancel-button"
                          style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
                          onClick={() => { setLogoPreview(null); setNewTeam(p => ({ ...p, logo: null })); }}>
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <div className="form-row">
                      <input type="text" placeholder="Nom de l'équipe"
                        value={newTeam.name}
                        onChange={e => setNewTeam({ ...newTeam, name: e.target.value })} required />
                      <input type="text" placeholder="Tag (3-5 car.)" maxLength="5"
                        value={newTeam.tag}
                        onChange={e => setNewTeam({ ...newTeam, tag: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="form-buttons">
                      <button type="submit" className="submit-button" disabled={logoUploading}>Créer</button>
                      <button type="button" className="cancel-button"
                        onClick={() => { setShowCreateForm(false); setLogoPreview(null); setNewTeam({ name: '', tag: '', logo: null }); }}>
                        Annuler
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Teams grid */}
            {loadingTeams ? (
              <div className="loading">Chargement…</div>
            ) : teams.length === 0 ? (
              <div className="empty-state" style={{ padding: '2rem 0' }}>
                <Shield size={48} style={{ opacity: 0.3 }} />
                <p>Aucune équipe pour le moment</p>
                <p style={{ fontSize: '0.85rem' }}>Créez une équipe ou attendez une invitation</p>
              </div>
            ) : (
              <div className="teams-grid-v2">
                {teams.map((team, i) => {
                  const isOwner = team.owner?._id === user?._id || team.owner === user?._id;
                  const isExpanded = expandedTeam === team._id;
                  const isInviting = inviteTeamId === team._id;
                  return (
                    <motion.div key={team._id}
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="team-card-v2">

                      {/* Owner crown */}
                      {isOwner && (
                        <span className="team-owner-badge">
                          <Crown size={11} /> OWNER
                        </span>
                      )}

                      {/* Header */}
                      <div className="team-card-v2-header">
                        {team.logo ? (
                          <img src={team.logo} alt={team.name} className="team-logo-sm"
                            onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="team-logo-placeholder-sm">
                            <Shield size={20} />
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <p className="team-card-v2-name">{team.name}</p>
                          <p className="team-card-v2-meta">
                            {team.tag && <span className="team-tag" style={{ marginRight: 6 }}>{team.tag}</span>}
                            <Users size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />
                            {team.members?.length || 0} membre{(team.members?.length || 0) > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      {/* Action bar */}
                      <div className="team-card-v2-footer">
                        <button className="team-btn team-btn-secondary"
                          onClick={() => setExpandedTeam(isExpanded ? null : team._id)}>
                          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          Membres
                        </button>
                        <Link to={`/team/${team._id}`} className="team-btn team-btn-secondary">
                          <ChevronRight size={13} /> Page équipe
                        </Link>
                        {isOwner && (
                          <>
                            <button className="team-btn team-btn-accent"
                              onClick={() => setInviteTeamId(isInviting ? null : team._id)}>
                              <Mail size={13} /> Inviter
                            </button>
                            <button className="team-btn team-btn-danger"
                              onClick={() => handleDeleteTeam(team._id)}>
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>

                      {/* Invite form */}
                      <AnimatePresence>
                        {isInviting && (
                          <motion.form
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }} onSubmit={handleInvite}
                            style={{ overflow: 'hidden' }}>
                            <div className="team-invite-form">
                              <p className="team-invite-label">Email ou pseudo Discord du joueur</p>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input type="text" className="team-invite-input"
                                  placeholder="joueur@mail.com ou PseudoDiscord"
                                  value={inviteEmail}
                                  onChange={e => setInviteEmail(e.target.value)} required />
                                <button type="submit" className="submit-button"
                                  style={{ padding: '0.45rem 0.9rem', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                  Envoyer
                                </button>
                              </div>
                            </div>
                          </motion.form>
                        )}
                      </AnimatePresence>

                      {/* Members list */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
                            <div className="team-members-list">
                              {team.members?.length > 0 ? team.members.map(m => (
                                <div key={m.user?._id || m._id} className="team-member-row">
                                  <Users size={13} style={{ color: 'var(--color-accent-1)', flexShrink: 0 }} />
                                  <span>{m.user?.displayName || m.user?.username || 'Inconnu'}</span>
                                  {m.user?._id === team.owner?._id && (
                                    <Crown size={11} style={{ color: 'var(--color-accent-1)', marginLeft: 'auto' }} />
                                  )}
                                </div>
                              )) : (
                                <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: 0 }}>
                                  Aucun membre
                                </p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Tournois */}
          <section className="dash-card">
            <div className="dash-section-header">
              <h2 className="dash-section-title"><Trophy size={18} /> Tournois actifs</h2>
              <Link to="/tournaments" className="dash-link-btn">
                Voir tous <ChevronRight size={14} />
              </Link>
            </div>
            <div className="empty-state" style={{ padding: '1.5rem 0' }}>
              <Swords size={36} style={{ opacity: 0.25 }} />
              <p style={{ fontSize: '0.9rem' }}>Aucun tournoi actif</p>
            </div>
          </section>
        </div>

        {/* ── SIDEBAR ── */}
        <div className="dashboard-sidebar">

          {/* Invitations reçues */}
          <div className="dash-card">
            <div className="dash-section-header" style={{ marginBottom: '1rem' }}>
              <h2 className="dash-section-title">
                <Bell size={16} /> Invitations
                {invitations.length > 0 && (
                  <span className="invite-count-badge">{invitations.length}</span>
                )}
              </h2>
            </div>

            {loadingInvites ? (
              <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Chargement…</div>
            ) : invitations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1rem 0', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                <Bell size={28} style={{ opacity: 0.2, display: 'block', margin: '0 auto 0.5rem' }} />
                Aucune invitation en attente
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {invitations.map(inv => (
                  <motion.div key={inv._id || inv.invitationToken}
                    initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                    className="inv-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                      {inv.team?.logo ? (
                        <img src={inv.team.logo} alt={inv.team.name}
                          style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'contain', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(164,107,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Shield size={14} style={{ color: 'var(--color-accent-1)' }} />
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <p className="inv-team-name">{inv.team?.name || 'Équipe inconnue'}</p>
                        <p className="inv-by">de {inv.invitedBy?.displayName || inv.invitedBy?.username || '—'}</p>
                      </div>
                    </div>
                    <div className="inv-actions">
                      <button className="inv-accept-btn"
                        onClick={() => handleAcceptInvitation(inv.invitationToken)}>
                        <CheckCircle size={13} /> Accepter
                      </button>
                      <button className="inv-decline-btn"
                        onClick={() => handleDeclineInvitation(inv.invitationToken)}>
                        <XCircle size={13} /> Refuser
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Actions rapides */}
          <div className="dash-card">
            <div className="dash-section-header" style={{ marginBottom: '0.75rem' }}>
              <h2 className="dash-section-title"><Zap size={16} /> Actions rapides</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <Link to="/teams" className="quick-action-link">
                <Users size={15} /> Parcourir les équipes
                <ChevronRight size={14} style={{ marginLeft: 'auto', opacity: 0.5 }} />
              </Link>
              <Link to="/tournaments" className="quick-action-link">
                <Trophy size={15} /> Voir les tournois
                <ChevronRight size={14} style={{ marginLeft: 'auto', opacity: 0.5 }} />
              </Link>
              <Link to="/profile" className="quick-action-link">
                <Shield size={15} /> Mon profil Riot
                <ChevronRight size={14} style={{ marginLeft: 'auto', opacity: 0.5 }} />
              </Link>
            </div>
          </div>

          {/* Stats summary */}
          {!loadingStats && stats && (
            <div className="dash-card">
              <div className="dash-section-header" style={{ marginBottom: '0.75rem' }}>
                <h2 className="dash-section-title"><Star size={16} /> Statistiques</h2>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {soloWR && (
                  <div className="stat-row">
                    <span className="stat-row-label">Solo/Duo</span>
                    <span className="stat-row-value" style={{ color: soloWR.wr >= 50 ? '#10b981' : '#ef4444' }}>
                      {soloWR.wr}% — {soloWR.total} parties
                    </span>
                  </div>
                )}
                {flexWR && (
                  <div className="stat-row">
                    <span className="stat-row-label">Flex</span>
                    <span className="stat-row-value" style={{ color: flexWR.wr >= 50 ? '#10b981' : '#ef4444' }}>
                      {flexWR.wr}% — {flexWR.total} parties
                    </span>
                  </div>
                )}
                {stats?.masteries?.length > 0 && (
                  <div className="stat-row">
                    <span className="stat-row-label">Champions maîtrisés</span>
                    <span className="stat-row-value">{stats.masteries.length}</span>
                  </div>
                )}
                <div className="stat-row">
                  <span className="stat-row-label">Équipes</span>
                  <span className="stat-row-value">{teams.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
